import React from 'react';
import { motion } from 'framer-motion';
import { X, Medal, Trophy, Gem, TrendingUp, DollarSign, ArrowRight } from 'lucide-react';
interface CompensationPlanModalProps {
  onClose: () => void;
}
export function CompensationPlanModal({
  onClose
}: CompensationPlanModalProps) {
  return <motion.div initial={{
    width: 0,
    opacity: 0
  }} animate={{
    width: 420,
    opacity: 1
  }} exit={{
    width: 0,
    opacity: 0
  }} transition={{
    type: 'spring',
    damping: 28,
    stiffness: 300
  }} className="h-full flex-shrink-0 overflow-hidden" data-id="element-203">
      <div className="w-[420px] h-full bg-slate-900 border-l border-slate-700 overflow-y-auto relative flex flex-col" data-id="element-204">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-50 pointer-events-none" data-id="element-205" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" data-id="element-206" />

        {/* Close Button */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-5 py-3 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800" data-id="element-207">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider relative z-10" data-id="element-208">
            Compensation Plan
          </h3>
          <button onClick={onClose} className="p-1.5 bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors border border-slate-700 relative z-10" data-id="element-209">
            <X size={16} data-id="element-210" />
          </button>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex-1 overflow-y-auto" data-id="element-211">
          {/* Header */}
          <div className="text-center pt-6 pb-4 px-5 border-b border-slate-800/50" data-id="element-212">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg shadow-blue-900/50 mb-4 border border-blue-400/20" data-id="element-213">
              <img src="https://cdn.magicpatterns.com/uploads/8NoVCvBe7DuNgWECKxstsS/R_Platform_Logo.png" alt="Rapid" className="w-10 h-10 object-contain brightness-0 invert" data-id="element-214" />
            </div>
            <h2 className="text-lg font-bold text-white tracking-widest uppercase mb-1" data-id="element-215">
              Rapid Home Lending
            </h2>
            <div className="h-px w-24 mx-auto bg-gradient-to-r from-transparent via-blue-500 to-transparent my-2" data-id="element-216" />
            <h3 className="text-sm font-semibold text-blue-400 tracking-widest uppercase" data-id="element-217">
              2026 Compensation Plan
            </h3>
          </div>

          <div className="p-5 space-y-7" data-id="element-218">
            {/* GOLD TIER */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className="relative" data-id="element-219">
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-220">
                <div className="p-1.5 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 shadow-lg shadow-amber-900/40" data-id="element-221">
                  <Medal size={18} className="text-white" data-id="element-222" />
                </div>
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500 uppercase tracking-wider" data-id="element-223">
                  Gold
                </h3>
              </div>

              <div className="text-center mb-2" data-id="element-224">
                <span className="px-2.5 py-0.5 rounded-full bg-slate-800/80 border border-slate-700 text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-225">
                  LOA Only
                </span>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-700 shadow-xl bg-slate-800/30 backdrop-blur-sm" data-id="element-226">
                <div className="grid grid-cols-3 divide-x divide-slate-700 border-b border-slate-700 bg-slate-800/80" data-id="element-227">
                  <div className="p-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-228">
                    1-3 Units
                  </div>
                  <div className="p-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-229">
                    4-8 Units
                  </div>
                  <div className="p-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-230">
                    9+ Units
                  </div>
                </div>
                <div className="grid grid-cols-3 divide-x divide-slate-700 bg-gradient-to-b from-slate-800/40 to-slate-900/40" data-id="element-231">
                  <div className="p-3 text-center text-base font-bold text-white" data-id="element-232">
                    70 BPS
                  </div>
                  <div className="p-3 text-center text-base font-bold text-white" data-id="element-233">
                    80 BPS
                  </div>
                  <div className="p-3 text-center text-base font-bold text-white" data-id="element-234">
                    90 BPS
                  </div>
                </div>
              </div>
            </motion.div>

            {/* PLATINUM TIER */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="relative" data-id="element-235">
              <div className="flex items-center justify-center gap-2 mb-4 bg-slate-900 w-fit mx-auto px-4" data-id="element-236">
                <div className="p-1.5 rounded-full bg-gradient-to-br from-slate-300 to-slate-500 shadow-lg shadow-slate-700/40" data-id="element-237">
                  <Trophy size={18} className="text-white" data-id="element-238" />
                </div>
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-200 via-slate-300 to-slate-400 uppercase tracking-wider" data-id="element-239">
                  Platinum
                </h3>
              </div>

              <div className="space-y-2" data-id="element-240">
                {/* LOA Table */}
                <div className="overflow-hidden rounded-lg border border-slate-700 shadow-xl bg-slate-800/30 backdrop-blur-sm" data-id="element-241">
                  <div className="p-1.5 text-center bg-slate-800/80 border-b border-slate-700 font-bold text-slate-300 uppercase tracking-wider text-xs" data-id="element-242">
                    LOA
                  </div>
                  <div className="divide-y divide-slate-700/50" data-id="element-243">
                    <div className="flex justify-between p-2 px-4" data-id="element-244">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-245">
                        1-3 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-246">
                        80 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-247">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-248">
                        4-8 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-249">
                        90 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-250">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-251">
                        9+ Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-252">
                        100 BPS
                      </span>
                    </div>
                  </div>
                </div>

                {/* LO Table */}
                <div className="overflow-hidden rounded-lg border border-slate-700 shadow-xl bg-slate-800/30 backdrop-blur-sm relative" data-id="element-253">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-400/5 to-transparent pointer-events-none" data-id="element-254" />
                  <div className="p-1.5 text-center bg-slate-800/90 border-b border-slate-700 font-bold text-white uppercase tracking-wider text-xs" data-id="element-255">
                    LO (Loan Officer)
                  </div>
                  <div className="divide-y divide-slate-700/50" data-id="element-256">
                    <div className="flex justify-between p-2 px-4" data-id="element-257">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-258">
                        1-3 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-259">
                        100 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-260">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-261">
                        4-8 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-262">
                        110 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-263">
                      <span className="text-slate-400 font-medium text-sm" data-id="element-264">
                        9+ Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-265">
                        120 BPS
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* DIAMOND TIER */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="relative" data-id="element-266">
              <div className="flex items-center justify-center gap-2 mb-4 bg-slate-900 w-fit mx-auto px-4" data-id="element-267">
                <div className="p-1.5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-900/40 animate-pulse" data-id="element-268">
                  <Gem size={18} className="text-white" data-id="element-269" />
                </div>
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-blue-400 to-indigo-400 uppercase tracking-wider" data-id="element-270">
                  Diamond
                </h3>
              </div>

              <div className="space-y-2" data-id="element-271">
                {/* LOA Table */}
                <div className="overflow-hidden rounded-lg border border-blue-900/50 shadow-xl bg-slate-800/30 backdrop-blur-sm" data-id="element-272">
                  <div className="p-1.5 text-center bg-slate-800/80 border-b border-blue-900/30 font-bold text-blue-200 uppercase tracking-wider text-xs" data-id="element-273">
                    LOA
                  </div>
                  <div className="divide-y divide-blue-900/20" data-id="element-274">
                    <div className="flex justify-between p-2 px-4" data-id="element-275">
                      <span className="text-blue-300/70 font-medium text-sm" data-id="element-276">
                        1-3 Units
                      </span>
                      <span className="text-blue-100 font-bold text-sm" data-id="element-277">
                        90 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-278">
                      <span className="text-blue-300/70 font-medium text-sm" data-id="element-279">
                        4-8 Units
                      </span>
                      <span className="text-blue-100 font-bold text-sm" data-id="element-280">
                        100 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-281">
                      <span className="text-blue-300/70 font-medium text-sm" data-id="element-282">
                        9+ Units
                      </span>
                      <span className="text-blue-100 font-bold text-sm" data-id="element-283">
                        110 BPS
                      </span>
                    </div>
                  </div>
                </div>

                {/* LO Table */}
                <div className="overflow-hidden rounded-lg border border-blue-500/30 shadow-xl shadow-blue-900/20 bg-slate-800/40 backdrop-blur-sm relative" data-id="element-284">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent pointer-events-none" data-id="element-285" />
                  <div className="p-1.5 text-center bg-blue-900/40 border-b border-blue-500/30 font-bold text-white uppercase tracking-wider text-xs" data-id="element-286">
                    LO (Loan Officer)
                  </div>
                  <div className="divide-y divide-blue-500/20" data-id="element-287">
                    <div className="flex justify-between p-2 px-4" data-id="element-288">
                      <span className="text-blue-200/70 font-medium text-sm" data-id="element-289">
                        1-3 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-290">
                        110 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-291">
                      <span className="text-blue-200/70 font-medium text-sm" data-id="element-292">
                        4-8 Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-293">
                        120 BPS
                      </span>
                    </div>
                    <div className="flex justify-between p-2 px-4" data-id="element-294">
                      <span className="text-blue-200/70 font-medium text-sm" data-id="element-295">
                        9+ Units
                      </span>
                      <span className="text-white font-bold text-sm" data-id="element-296">
                        130 BPS
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CAREER PATH */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="pt-4 border-t border-slate-800" data-id="element-297">
              <div className="flex items-center gap-2 mb-3" data-id="element-298">
                <TrendingUp size={16} className="text-blue-400" data-id="element-299" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-300">
                  Career Path
                </h4>
              </div>

              <div className="space-y-2" data-id="element-301">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-800/50 border border-slate-700" data-id="element-302">
                  <div className="p-1 rounded-full bg-yellow-500/20" data-id="element-303">
                    <ArrowRight size={12} className="text-yellow-500" data-id="element-304" />
                  </div>
                  <div data-id="element-305">
                    <p className="text-xs font-bold text-white" data-id="element-306">
                      Gold → Platinum
                    </p>
                    <p className="text-[10px] text-slate-400" data-id="element-307">
                      90 Days + $3M Funded
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-800/50 border border-slate-700" data-id="element-308">
                  <div className="p-1 rounded-full bg-blue-500/20" data-id="element-309">
                    <ArrowRight size={12} className="text-blue-400" data-id="element-310" />
                  </div>
                  <div data-id="element-311">
                    <p className="text-xs font-bold text-white" data-id="element-312">
                      Platinum → Diamond
                    </p>
                    <p className="text-[10px] text-slate-400" data-id="element-313">
                      12 Months + $15M Funded
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* VOLUME BONUS */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-314">
              <div className="flex items-center gap-2 mb-3" data-id="element-315">
                <DollarSign size={16} className="text-emerald-400" data-id="element-316" />
                <h4 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-317">
                  Volume Bonus
                </h4>
              </div>

              <div className="space-y-1.5 bg-slate-800/30 p-3 rounded-lg border border-slate-700/50" data-id="element-318">
                <div className="flex justify-between items-center py-0.5" data-id="element-319">
                  <span className="text-slate-300 font-medium text-sm" data-id="element-320">
                    $1M Funded
                  </span>
                  <span className="text-emerald-400 font-bold text-sm" data-id="element-321">
                    $1,000
                  </span>
                </div>
                <div className="h-px bg-slate-700/50 w-full" data-id="element-322" />
                <div className="flex justify-between items-center py-0.5" data-id="element-323">
                  <span className="text-slate-300 font-medium text-sm" data-id="element-324">
                    $2M Funded
                  </span>
                  <span className="text-emerald-400 font-bold text-sm" data-id="element-325">
                    $2,000
                  </span>
                </div>
                <div className="h-px bg-slate-700/50 w-full" data-id="element-326" />
                <div className="flex justify-between items-center py-0.5" data-id="element-327">
                  <span className="text-slate-300 font-medium text-sm" data-id="element-328">
                    $3M Funded
                  </span>
                  <span className="text-emerald-400 font-bold text-sm" data-id="element-329">
                    $3,000
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Footer PAR */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55
          }} className="text-center pt-4 pb-2 border-t border-slate-800" data-id="element-330">
              <div className="inline-block px-5 py-1.5 rounded-full bg-blue-900/30 border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.2)]" data-id="element-331">
                <span className="text-blue-200 font-medium uppercase tracking-widest text-xs" data-id="element-332">
                  New PAR:{' '}
                </span>
                <span className="text-white font-bold text-base ml-1.5" data-id="element-333">
                  2.75
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>;
}